import styled from '@emotion/styled';
import { Trans } from 'react-i18next';
import { useState } from 'react';
import { Alert, Icon, Link, Loader, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix, useHelpHotjarUrl } from '@hotjar/translations';
import { useAccountTrialInfo } from '@hotjar/account';
import type {
  BillingPeriod,
  ProductAllowance,
} from '~/subscription-management/api/common/model/subscription';
import { BillingPeriodSelector } from './components/billing-period-selector/BillingPeriodSelector';
import { Heading } from '../EditOrderContainer.styles';
import { OrganizationSummary } from '~/subscription-management/features/service-resource-list';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { CONTACT_SUPPORT_LINK } from '~/subscription-management/constants/links';
import type { OrganizationListItem } from '~/subscription-management/features/service-resource-list/hooks/useServiceResourceList';

interface Props {
  allResources?: OrganizationListItem[];
  loading: boolean;
  isPricePreviewLoading: boolean;
  updatedProductAllowances: ProductAllowance[];
  billingPeriod: BillingPeriod;
  currency: Currency;
  hasActiveSubscription: boolean;
  shouldDisableCheckout: boolean;
  loadedFromDraftOrder: boolean;
  subscriptionEndDate?: string;
  canProceed: boolean;
  onBillingPeriodChanged(billingPeriod: BillingPeriod): void;
  onPlanChange(plan: ProductAllowance): void;
  onProceed(): void;
}

const S = {
  SubHeadingContainer: styled(Stack)`
    margin-top: ${tokens.spacer.x6};
    margin-bottom: ${tokens.spacer.x3};
    align-items: center;
    gap: ${tokens.spacer.x1};
  `,
  CheckIcon: styled(Icon)`
    margin-left: ${tokens.spacer.x2};
  `,
  OrganizationSummaryStack: styled(Stack)`
    &:last-child {
      margin-bottom: ${tokens.spacer.x3};
    }
  `,
  Alert: styled(Alert)`
    margin-bottom: ${tokens.spacer.x3};
  `,
};

export function ManageSubscriptions({
  allResources,
  loading,
  isPricePreviewLoading,
  billingPeriod,
  currency,
  updatedProductAllowances,
  hasActiveSubscription,
  shouldDisableCheckout,
  loadedFromDraftOrder,
  subscriptionEndDate,
  canProceed,
  onBillingPeriodChanged,
  onPlanChange,
  onProceed,
}: Props): JSX.Element {
  const [tHeader, tSubscriptions, tAlerts] = useTranslationWithPrefix([
    'checkout:header',
    'checkout:wizard.subscriptions',
    'checkout:alerts.pre-selected-plans',
  ]);
  const [canShowPreselectedAlerts, setCanShowPreselectedAlerts] = useState(true);
  const trialInfo = useAccountTrialInfo();
  const helpHotjarUrl = useHelpHotjarUrl();

  return (
    <>
      <Heading textType="header4" as="h1">
        {tHeader('manage-subscriptions')}
      </Heading>
      <Loader size="xl" isLoading={loading}>
        {shouldDisableCheckout && (
          <S.Alert kind="info">
            <Trans i18nKey="checkout:wizard.subscriptions.cancelled-subscription-alert">
              We are sorry but, at the moment, it’s not possible to edit your plans from our app.
              Please
              <Link kind="inline" href={`${helpHotjarUrl}${CONTACT_SUPPORT_LINK}`}>
                contact support
              </Link>
              to upgrade your plans
            </Trans>
          </S.Alert>
        )}

        {canShowPreselectedAlerts && (
          <>
            {trialInfo?.activeOrEligible === true && (
              <S.Alert
                kind="success"
                actionText={canProceed ? tAlerts('continue') : undefined}
                onAction={onProceed}
              >
                {tAlerts(loadedFromDraftOrder ? 'trial-and-other-changes' : 'trial-only')}
              </S.Alert>
            )}

            {loadedFromDraftOrder && trialInfo?.activeOrEligible === false && (
              <S.Alert kind="success">{tAlerts('non-trial')}</S.Alert>
            )}
          </>
        )}

        {!hasActiveSubscription && (
          <BillingPeriodSelector
            billingPeriod={billingPeriod}
            onSelect={onBillingPeriodChanged}
            showYearlyDiscount={!hasActiveSubscription}
          />
        )}

        <S.SubHeadingContainer>
          <Text textType="subtitle1">{tSubscriptions('subscriptions')}</Text>
        </S.SubHeadingContainer>

        <S.OrganizationSummaryStack direction="column" spacing="x3">
          {allResources?.map((organization) => (
            <OrganizationSummary
              editable={shouldDisableCheckout ? 'DISABLED' : 'EDITABLE'}
              onPlanChange={(plan) => {
                setCanShowPreselectedAlerts(false);
                onPlanChange(plan);
              }}
              organization={organization}
              updatedProducts={updatedProductAllowances}
              billingPeriod={billingPeriod}
              currency={currency}
              subscriptionEndDate={subscriptionEndDate}
              key={organization.id}
              showBundleDiscount={!isPricePreviewLoading}
            />
          ))}
        </S.OrganizationSummaryStack>
      </Loader>
    </>
  );
}
